import React, { useMemo, useRef, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, GridApi, GridReadyEvent, IDatasource, IGetRowsParams } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { agGridLocaleFr } from '@/config/ag-grid-locale-fr'
import { AGTableWrapper } from '@/components/ag-grid/AGTableWrapper'
import { getBackOfficeLogsColumnDefs } from '@/column-defs/activity-logs.column-defs'
import { activityLogsService } from '@/services/activity-logs.service'

export const BackOfficeLogsTab: React.FC = () => {
  const gridApiRef = useRef<GridApi | null>(null)

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api

    // Create datasource for infinite row model (available in Community edition)
    const datasource: IDatasource = {
      getRows: async (params: IGetRowsParams) => {
        try {
          // Convert ag-grid request to page/pageSize
          const startRow = params.startRow ?? 0
          const endRow = params.endRow ?? 20
          const pageSize = endRow - startRow
          const page = Math.floor(startRow / pageSize)

          // Extract sort information
          let sort: string | undefined
          let sortDirection: -1 | 1 | undefined
          if (params.sortModel && params.sortModel.length > 0) {
            const firstSort = params.sortModel[0]
            sort = firstSort.colId
            sortDirection = firstSort.sort === 'asc' ? 1 : -1
          }

          // Extract filter information with filter type
          const filter: Record<string, { type: string; value: unknown }> = {}
          if (params.filterModel) {
            Object.keys(params.filterModel).forEach(colId => {
              const filterModel = params.filterModel[colId]
              
              // Handle different filter types
              if (filterModel.filterType === 'text') {
                // Text filter - send type and value
                if (filterModel.filter !== null && filterModel.filter !== undefined && filterModel.filter !== '') {
                  const filterType = filterModel.type || 'contains'
                  filter[colId] = {
                    type: filterType,
                    value: filterModel.filter,
                  }
                }
              } else if (filterModel.filterType === 'number') {
                // Number filter - send type and value
                if (filterModel.filter !== null && filterModel.filter !== undefined && filterModel.filter !== '') {
                  const filterType = filterModel.type || 'equals'
                  filter[colId] = {
                    type: filterType,
                    value: Number(filterModel.filter),
                  }
                  
                  // Handle range filters
                  if (filterType === 'inRange' && filterModel.filterTo !== null && filterModel.filterTo !== undefined) {
                    filter[colId] = {
                      type: 'inRange',
                      value: {
                        from: Number(filterModel.filter),
                        to: Number(filterModel.filterTo),
                      },
                    }
                  }
                }
              } else if (filterModel.filterType === 'date') {
                // Date filter - send type and value
                if (filterModel.dateFrom) {
                  const filterType = filterModel.type || 'equals'
                  filter[colId] = {
                    type: filterType,
                    value: filterModel.dateFrom,
                  }
                  
                  // Handle date range
                  if (filterType === 'inRange' && filterModel.dateTo) {
                    filter[colId] = {
                      type: 'inRange',
                      value: {
                        from: filterModel.dateFrom,
                        to: filterModel.dateTo,
                      },
                    }
                  }
                }
              } else if (filterModel.filterType === 'set') {
                // Set filter - send type and array of values
                if (filterModel.values && filterModel.values.length > 0) {
                  filter[colId] = {
                    type: 'in',
                    value: filterModel.values,
                  }
                }
              } else {
                // Fallback: use filter value directly with default type
                if (filterModel.filter !== null && filterModel.filter !== undefined && filterModel.filter !== '') {
                  filter[colId] = {
                    type: filterModel.type || 'equals',
                    value: filterModel.filter,
                  }
                } else if (filterModel.values && filterModel.values.length > 0) {
                  filter[colId] = {
                    type: 'in',
                    value: filterModel.values,
                  }
                }
              }
            })
          }

          // Fetch data from API with sort and filter
          const response = await activityLogsService.getBackOfficeLogs({
            page,
            limit: pageSize,
            sort,
            sortDirection,
            filter: Object.keys(filter).length > 0 ? filter : undefined,
          })

          // Call success callback with the data
          params.successCallback(response.rows, response.count)
        } catch (error) {
          console.error('Error loading backoffice logs:', error)
          params.failCallback()
        }
      },
    }

    // Set the datasource for infinite row model
    params.api.setGridOption('datasource', datasource)
  }, [])

  const columnDefs: ColDef[] = useMemo(() => getBackOfficeLogsColumnDefs(), [])

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  )

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
          Journaux d'activité - BackOffice
        </h2>
      </div>

      <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div
          className="ag-theme-alpine"
          style={{ height: '600px', width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <AGTableWrapper collection="activity-logs-backoffice">
            <AgGridReact
              theme="legacy"
              rowModelType="infinite"
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              cacheBlockSize={20}
              maxBlocksInCache={10}
              onGridReady={handleGridReady}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressCellFocus={false}
              headerHeight={48}
              rowHeight={48}
              localeText={agGridLocaleFr}
            />
          </AGTableWrapper>
        </div>
      </div>
    </div>
  )
}
